import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import ProgressButton from '@/components/common/progress-button';
import SearchInput from '@/components/common/search-input';
import { ContactList, type ContactListRef } from '@/components/contacts/contact-list/contact-list';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useSendContact from '@/hooks/conversations/use-send-contact';
import useSearchFriends from '@/hooks/search/use-search-friends';
import { useSessionMessages } from '@/hooks/sessions/use-session-messages';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { addToast } from '@/stores/toast';

interface Props {
  type: ConversationType;
}

export default function SendContactRoute({ type }: Props) {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.CONTACT || ROUTES.MESSAGES.GROUPS.CONTACT);
  const groupListRef = useRef<ContactListRef>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFriendId, setSelectedFriendId] = useState<number>();
  const { filteredFriends } = useSearchFriends(searchTerm);
  const { session } = useSessionMessages(type === 'session' ? id : undefined);
  const { sendContact, isLoading } = useSendContact(type, id);

  const isDesktop = useIsDesktop();

  const disabledUserIds = type === 'session' && session?.UserID ? [session.UserID] : [];

  const handleSelectFriends = (friendIds: number[]) => {
    setSelectedFriendId(friendIds[0]);
  };

  const handleSend = () => {
    if (sendContact(selectedFriendId)) {
      addToast({
        type: 'success',
        message: t('common.done'),
      });

      navigate(-1);
    }
  };

  return (
    <PageLayout
      title={t('conversation.contact.title')}
      className='flex flex-1 flex-col'
      headerConfig={{
        navigateBack: !isDesktop || undefined,
        actions: (
          <ProgressButton
            as='button'
            isLoading={isLoading}
            disabled={!selectedFriendId}
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            onClick={handleSend}
            label='common.share'
          />
        ),
        elementAfterNav: (
          <SearchInput
            className='mx-5 xs:mx-5 my-0 mt-4'
            keyword={searchTerm}
            setKeyword={setSearchTerm}
            clearKeyword={searchTerm ? () => setSearchTerm('') : undefined}
          />
        ),
      }}>
      <LoadingLayout promise={loaderData?.promises}>
        {() => (
          <ContactList
            singleSelect
            ref={groupListRef}
            friends={filteredFriends}
            onSelectFriends={handleSelectFriends}
            disabledUserIds={disabledUserIds}
          />
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
